import { useEffect, useRef } from 'react'
import { useFocusTrap } from '@/lib/useFocusTrap'
import { AnimatePresence, motion } from 'framer-motion'
import type { GalleryItem } from '@/content/gallery'
import { smoothEase } from '@/lib/motion'

type GalleryPhotoLightboxProps = {
  item: GalleryItem | null
  onClose: () => void
  reduced: boolean
}

/** Full-screen enlarge — same interaction as the main gallery grid. */
export function GalleryPhotoLightbox({ item, onClose, reduced }: GalleryPhotoLightboxProps) {
  const dialogRef = useRef<HTMLDivElement>(null)

  useFocusTrap({
    active: Boolean(item),
    containerRef: dialogRef,
    onEscape: onClose,
  })

  useEffect(() => {
    if (!item) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [item, onClose])

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged photo"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0 : 0.2 }}
        >
          <motion.div
            ref={dialogRef}
            initial={reduced ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
            transition={{ duration: reduced ? 0 : 0.28, ease: smoothEase }}
            className="relative max-h-[90vh] max-w-[min(100%,960px)] overflow-hidden rounded-[var(--radius-card)] bg-white shadow-[var(--shadow-card)]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={item.src}
              alt={item.alt}
              width={item.width}
              height={item.height}
              className="max-h-[85vh] w-auto object-contain"
            />
            {item.badge && (
              <span className="absolute left-4 top-4 rounded-full border border-hairline bg-canvas/95 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-ink shadow-sm">
                {item.badge}
              </span>
            )}
            <button
              type="button"
              className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-ink shadow-[var(--shadow-card)] transition-transform duration-200 hover:scale-105 hover:bg-white focus-visible:ring-2 focus-visible:ring-primary"
              onClick={onClose}
              aria-label="Close"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
                <path
                  fill="currentColor"
                  d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L12 13.41l-6.29 6.3-1.42-1.41L10.59 12 4.3 5.71 5.71 4.3 12 10.59l6.29-6.3z"
                />
              </svg>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
